import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Input, Select, Space, Typography, Tag, Spin, message, Modal, Tooltip, Switch } from "antd";
import { Search, Upload, Eye, Plus, Edit, Trash2, Star, Rocket, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../helper/apiClient";
import ShoseImage from "../../assets/images/shose-image.avif";
import apiPath from "../../constants/apiPath";

const { Title, Text } = Typography;
const { Option } = Select;

const ProductsIndex = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        status: "all",
        category: "all",
        featured: "all",
        bestseller: "all",
        limited: "all",
        stock: "all",
    });
    const [categories, setCategories] = useState([]);

    // Debounce search text
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Fetch categories for filter dropdown
    const fetchCategories = async () => {
        try {
            const response = await apiClient.get(`${apiPath.activeCategoryList}`);

            if (response.data?.success) {
                const categoryList = response.data.categories || response.data.data || [];
                setCategories(categoryList.filter(cat => cat.status === true || cat.status === "true"));
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // Toggle product status
    const toggleProductStatus = async (productId, currentStatus) => {
        Modal.confirm({
            title: `Confirm Status Change`,
            content: `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this product?`,
            okText: "Yes",
            cancelText: "No",
            onOk: async () => {
                setUpdatingStatusId(productId);
                try {
                    // Using apiPath as a function with the productId
                    const apiUrl = apiPath.marketPlaceProductStatus(productId);
                    console.log("Calling API:", apiUrl); // For debugging
                    
                    const response = await apiClient.put(apiUrl, { status: !currentStatus });

                    if (response.data?.success) {
                        message.success(response.data.message || `Product ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
                        // Refresh the current page to show updated status
                        fetchProducts(pagination.current, pagination.pageSize);
                    } else {
                        message.error(response.data?.message || "Failed to update product status");
                    }
                } catch (error) {
                    console.error("Error updating product status:", error);
                    message.error(error.response?.data?.message || "Failed to update product status");
                } finally {
                    setUpdatingStatusId(null);
                }
            },
        });
    };

    // Export products to CSV
    const exportProducts = async () => {
        try {
            setLoading(true);
            message.loading({ content: 'Preparing export...', key: 'export' });
            
            // Fetch all products without pagination for export
            const params = {
                page: 1,
                page_size: 10000, // Fetch all products
            };

            // Apply current filters to export filtered data
            if (debouncedSearchText) {
                params.search = debouncedSearchText;
            }

            if (filters.status !== "all") {
                params.status = filters.status === "active" ? true : false;
            }

            if (filters.category !== "all") {
                params.category = filters.category;
            }

            if (filters.featured !== "all") {
                params.featured = filters.featured === "true";
            }

            if (filters.bestseller !== "all") {
                params.bestseller = filters.bestseller === "true";
            }

            if (filters.limited !== "all") {
                params.limited_edition = filters.limited === "true";
            }

            if (filters.stock !== "all") {
                if (filters.stock === "in_stock") {
                    params.min_stock = 1;
                } else if (filters.stock === "out_of_stock") {
                    params.stock = 0;
                } else if (filters.stock === "low_stock") {
                    params.max_stock = 10;
                    params.min_stock = 1;
                }
            }

            const response = await apiClient.get(`${apiPath.marketPlaceProductList}`, { params });

            if (response.data?.success) {
                let productsData = [];
                
                if (response.data.data?.products) {
                    productsData = response.data.data.products;
                } else if (response.data.products) {
                    productsData = response.data.products;
                } else if (Array.isArray(response.data.data)) {
                    productsData = response.data.data;
                }

                if (productsData.length === 0) {
                    message.warning({ content: "No products to export", key: 'export' });
                    return;
                }

                // Convert to CSV
                const exportData = productsData.map(product => ({
                    'ID': product.id,
                    'Name': product.name,
                    'SKU': product.sku || 'N/A',
                    'Points': product.price,
                    'Stock Quantity': product.stock_quantity,
                    'Category': product.category_name || 'Uncategorized',
                    'Status': product.status ? 'Active' : 'Inactive',
                    'Featured': product.featured ? 'Yes' : 'No',
                    'Best Seller': product.bestseller ? 'Yes' : 'No',
                    'Limited Edition': product.limited_edition ? 'Yes' : 'No',
                    'Manufacturer': product.manufacturer || 'N/A',
                    'Description': (product.description || 'N/A').replace(/,/g, ';'), // Replace commas to avoid CSV issues
                    'Created At': product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'
                }));

                // Generate CSV content
                const headers = Object.keys(exportData[0]);
                const csvRows = [
                    headers.join(','),
                    ...exportData.map(row => 
                        headers.map(header => {
                            let value = row[header];
                            // Handle different data types
                            if (value === undefined || value === null) {
                                return '';
                            }
                            // Convert to string
                            value = String(value);
                            // Escape quotes and wrap in quotes if contains comma or newline
                            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                                return `"${value.replace(/"/g, '""')}"`;
                            }
                            return value;
                        }).join(',')
                    )
                ];
                
                const csvContent = csvRows.join('\n');
                
                // Add BOM for UTF-8 to handle special characters
                const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                link.setAttribute('href', url);
                link.setAttribute('download', `products_export_${timestamp}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                message.success({ content: `${productsData.length} products exported successfully`, key: 'export' });
            } else {
                message.error({ content: response.data?.message || "Failed to export products", key: 'export' });
            }
        } catch (error) {
            console.error("Error exporting products:", error);
            message.error({ content: error.response?.data?.message || "Failed to export products", key: 'export' });
        } finally {
            setLoading(false);
        }
    };

    // Fetch products from API
    const fetchProducts = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = {
                page: page,
                page_size: pageSize,
            };

            if (debouncedSearchText) {
                params.search = debouncedSearchText;
            }

            if (filters.status !== "all") {
                params.status = filters.status === "active" ? true : false;
            }

            if (filters.category !== "all") {
                params.category = filters.category;
            }

            if (filters.featured !== "all") {
                params.featured = filters.featured === "true";
            }

            if (filters.bestseller !== "all") {
                params.bestseller = filters.bestseller === "true";
            }

            if (filters.limited !== "all") {
                params.limited_edition = filters.limited === "true";
            }

            if (filters.stock !== "all") {
                if (filters.stock === "in_stock") {
                    params.min_stock = 1;
                } else if (filters.stock === "out_of_stock") {
                    params.stock = 0;
                } else if (filters.stock === "low_stock") {
                    params.max_stock = 10;
                    params.min_stock = 1;
                }
            }

            const response = await apiClient.get(`${apiPath.marketPlaceProductList}`, { params });

            if (response.data?.success) {
                let productsData = [];
                let totalCount = 0;

                if (response.data.data?.products) {
                    productsData = response.data.data.products;
                    totalCount = response.data.data.pagination?.total_items || 0;
                } else if (response.data.products) {
                    productsData = response.data.products;
                    totalCount = response.data.total || productsData.length;
                } else if (Array.isArray(response.data.data)) {
                    productsData = response.data.data;
                    totalCount = response.data.count || productsData.length;
                } else {
                    productsData = [];
                    totalCount = 0;
                }

                setProducts(productsData);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: totalCount,
                });
            } else {
                message.error(response.data?.message || "Failed to fetch products");
                setProducts([]);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            message.error(error.response?.data?.message || "Failed to load products");
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchText, filters]);

    // View product
    const handleViewProduct = async (productId) => {
        navigate(`/catalog/products/${productId}`);
    };

    // Edit product
    const handleEditProduct = (productId) => {
        navigate(`/catalog/products/edit/${productId}`);
    };

    // Delete product
    const deleteProduct = async (productId) => {
        Modal.confirm({
            title: "Are you sure?",
            content: "This action cannot be undone. This will permanently delete the product.",
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const response = await apiClient.delete(`${apiPath.marketPlaceProductDelete}${productId}/`);

                    if (response.data?.success) {
                        message.success("Product deleted successfully");
                        fetchProducts(pagination.current, pagination.pageSize);
                    } else {
                        message.error(response.data?.message || "Failed to delete product");
                    }
                } catch (error) {
                    console.error("Error deleting product:", error);
                    message.error(error.response?.data?.message || "Failed to delete product");
                }
            },
        });
    };

    // Handle page change
    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize: pageSize }));
        fetchProducts(page, pageSize);
    };

    // Handle filter changes
    const handleStatusFilterChange = (value) => {
        setFilters(prev => ({ ...prev, status: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleCategoryFilterChange = (value) => {
        setFilters(prev => ({ ...prev, category: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleFeaturedFilterChange = (value) => {
        setFilters(prev => ({ ...prev, featured: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleBestsellerFilterChange = (value) => {
        setFilters(prev => ({ ...prev, bestseller: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleLimitedFilterChange = (value) => {
        setFilters(prev => ({ ...prev, limited: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleStockFilterChange = (value) => {
        setFilters(prev => ({ ...prev, stock: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle clear filters
    const handleClearFilters = () => {
        setSearchText("");
        setDebouncedSearchText("");
        setFilters({
            status: "all",
            category: "all",
            featured: "all",
            bestseller: "all",
            limited: "all",
            stock: "all"
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Fetch products when filters or debounced search changes
    useEffect(() => {
        fetchProducts(1, pagination.pageSize);
    }, [debouncedSearchText, filters]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const columns = [
        {
            title: "Product",
            key: "product",
            width: 320,
            render: (_, record) => (
                <div className="product-info" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {record.images && record.images.length > 0 ? (
                        <img
                            src={record.images.find(img => img.is_primary)?.image_url || record.images[0]?.image_url}
                            alt={record.name}
                            style={{ width: 50, height: 50, objectFit: "cover", flex:"0 0 50px", borderRadius: 8 }}
                            onError={(e) => {
                                e.target.src = ShoseImage;
                            }}
                        />
                    ) : (
                        <img src={ShoseImage} alt="" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }} />
                    )}

                    <div>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>{record.name}</div>
                        <div style={{ fontSize: 12, color: "#666" }}>SKU: {record.sku || "N/A"}</div>
                        <div style={{ fontSize: 12, color: "#666" }}>Manufacturer: {record.manufacturer || "N/A"}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Points",
            key: "price",
            width: 120,
            align: "center",
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 16, color: "#ff4d4f" }}>
                        {parseFloat(record.price).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 12, color: "#666" }}>ID: {record.id}</div>
                </div>
            ),
        },
        {
            title: "Stock",
            key: "stock",
            width: 140,
            align: "center",
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500, fontSize: 18, color: "#1890ff" }}>
                        {record.stock_quantity}
                    </div>
                    <div>
                        {record.stock_quantity > 0 ? (
                            <Tag color="green" style={{ marginTop: 4 }}>
                                In Stock
                            </Tag>
                        ) : (
                            <Tag color="red" style={{ marginTop: 4 }}>
                                Out of Stock
                            </Tag>
                        )}
                    </div>
                    {record.stock_quantity <= 5 && record.stock_quantity > 0 && (
                        <Tag color="orange" style={{ marginTop: 4 }}>Low Stock ({record.stock_quantity})</Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Category",
            key: "category",
            width: 150,
            render: (_, record) => (
                <div>
                    <div>{record.category_name || "Uncategorized"}</div>
                </div>
            ),
        },
        {
            title: "Status",
            key: "status",
            width: 140,
            align: "center",
            render: (_, record) => (
                <div>
                    <Switch
                        checked={record.status}
                        onChange={() => toggleProductStatus(record.id, record.status)}
                        checkedChildren="Active"
                        unCheckedChildren="Inactive"
                        loading={updatingStatusId === record.id}
                        size="small"
                    />
                </div>
            ),
        },
        {
            title: "Featured",
            key: "featured",
            width: 100,
            align: "center",
            render: (_, record) => (
                <div>
                    {record.featured ? (
                        <Tooltip title="Featured Product">
                            <Tag color="gold" icon={<Star size={12} />} style={{ fontSize: 12 }}>
                                Featured
                            </Tag>
                        </Tooltip>
                    ) : (
                        <Tag color="default" style={{ fontSize: 12 }}>No</Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Best Seller",
            key: "bestseller",
            width: 110,
            align: "center",
            render: (_, record) => (
                <div>
                    {record.bestseller ? (
                        <Tooltip title="Best Selling Product">
                            <Tag color="cyan" icon={<Rocket size={12} />} style={{ fontSize: 12 }}>
                                Best Seller
                            </Tag>
                        </Tooltip>
                    ) : (
                        <Tag color="default" style={{ fontSize: 12 }}>No</Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Limited Edition",
            key: "limited_edition",
            width: 130,
            align: "center",
            render: (_, record) => (
                <div>
                    {record.limited_edition ? (
                        <Tooltip title="Limited Edition Product">
                            <Tag color="purple" icon={<Clock size={12} />} style={{ fontSize: 12 }}>
                                Limited
                            </Tag>
                        </Tooltip>
                    ) : (
                        <Tag color="default" style={{ fontSize: 12 }}>No</Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            align: "center",
            fixed: "right",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 4, justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View Product">
                        <Button
                            type="text"
                            icon={<Eye size={18} style={{ color: "#2563eb" }} />}
                            onClick={() => handleViewProduct(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Product">
                        <Button
                            type="text"
                            icon={<Edit size={18} style={{ color: "#10b981" }} />}
                            onClick={() => handleEditProduct(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Product">
                        <Button
                            type="text"
                            danger
                            icon={<Trash2 size={18} style={{ color: "#ef4444" }} />}
                            onClick={() => deleteProduct(record.id)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
          <div className="orders-container" style={{ padding: '24px', background: '#fff', borderRadius: "10px" }} >
            {/* Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                background: "#fff",
                borderRadius: 8,
                gap: 10,
                flexWrap: "wrap",
            }}>
                <Title level={3} style={{ margin: 0 }}>Products</Title>
                <Space>
                    <Button 
                        icon={<Upload size={16} />}
                        onClick={exportProducts}
                        loading={loading}
                    >
                        Export
                    </Button>
                    <Button
                        icon={<Plus size={16} />}
                        type="primary"
                        onClick={() => navigate("/catalog/products/add")}
                    >
                        Add Products
                    </Button>
                </Space>
            </div>

            {/* Filters */}
            <div style={{
                marginBottom: 20,
                background: "#fff",
                borderRadius: 8,
                gap: 16,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "space-between",
             
            }}>
                <Space size="large" wrap>
                    <Input
                        placeholder="Search by name or SKU"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        suffix={<Search size={16} color="#94a3b8" />}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Select
                        placeholder="Filter by status"
                        value={filters.status}
                        onChange={handleStatusFilterChange}
                        style={{ width: 130 }}
                        allowClear
                    >
                        <Option value="all">All Status</Option>
                        <Option value="active">Active</Option>
                        <Option value="inactive">Inactive</Option>
                    </Select>
                    <Select
                        placeholder="Filter by category"
                        value={filters.category}
                        onChange={handleCategoryFilterChange}
                        style={{ width: 180 }}
                        allowClear
                        showSearch
                        loading={categories.length === 0}
                    >
                        <Option value="all">All Categories</Option>
                        {categories.map(cat => (
                            <Option key={cat.id} value={cat.id}>
                                {cat.title}
                            </Option>
                        ))}
                    </Select>
                </Space>
                <Space>
                    <Button onClick={handleClearFilters}>
                        Clear All Filters
                    </Button>
                    {(filters.status !== "all" || filters.category !== "all" || filters.featured !== "all" ||
                        filters.bestseller !== "all" || filters.limited !== "all" || filters.stock !== "all" || searchText) && (
                            <Tag color="blue">Filters Applied</Tag>
                        )}
                </Space>
            </div>

            {/* Statistics Cards */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 16,
                marginBottom: 20
            }}>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9" , padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Total Products</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#1890ff" }}>
                        {pagination.total}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Active Products</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#52c41a" }}>
                        {products.filter(p => p.status).length}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Inactive Products</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#ff4d4f" }}>
                        {products.filter(p => !p.status).length}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Featured Products</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#faad14" }}>
                        {products.filter(p => p.featured).length}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Best Sellers</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#13c2c2" }}>
                        {products.filter(p => p.bestseller).length}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Limited Edition</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#722ed1" }}>
                        {products.filter(p => p.limited_edition).length}
                    </div>
                </div>
                <div style={{ background: "#fff",   boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 2px 0px", border:"1px solid #f9f9f9", padding: "16px", borderRadius: 8, textAlign: "center" }}>
                    <Text type="secondary">Low Stock Items</Text>
                    <div style={{ fontSize: 28, fontWeight: 600, color: "#fa8c16" }}>
                        {products.filter(p => p.stock_quantity > 0 && p.stock_quantity <= 5).length}
                    </div>
                </div>
            </div>

            {/* Table */}
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={products}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} products`,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    className="products-table"
                    style={{ background: "#fff", borderRadius: 8 }}
                    scroll={{ x: 1400 }}
                />
            </Spin>
        </div>
    );
};

export default ProductsIndex;